import { getSupabaseClient, hasSupabaseConfig } from "./supabaseClient";
import { calculateExpirationDate } from "../utils/expiration";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const DEMO_DATA_BASE_URL = joinUrl(
    process.env.REACT_APP_DEMO_DATA_BASE_URL || process.env.PUBLIC_URL || "",
    "DEMO_DATA"
);
const SUPABASE_CREDENTIALS_TABLE =
    process.env.REACT_APP_SUPABASE_CREDENTIALS_TABLE || "credentials";
const SUPABASE_PASSWORDS_TABLE =
    process.env.REACT_APP_SUPABASE_PASSWORDS_TABLE || "passwords";
const SUPABASE_INVITATIONS_TABLE =
    process.env.REACT_APP_SUPABASE_INVITATIONS_TABLE || "invitations";
const USE_SUPABASE =
    process.env.REACT_APP_USE_SUPABASE !== "false" && hasSupabaseConfig;
const USE_MOCK_API = !USE_SUPABASE && process.env.REACT_APP_USE_MOCK_API !== "false";
const MAX_USER_AGENT_LENGTH = 500;

const delay = (ms = 150) => new Promise((resolve) => setTimeout(resolve, ms));

function joinUrl(base, path) {
    const baseTrimmed = (base || "").replace(/\/+$/, "");
    const pathTrimmed = (path || "").replace(/^\/+/, "");
    if (!baseTrimmed) {
        return `/${pathTrimmed}`;
    }
    return `${baseTrimmed}/${pathTrimmed}`;
}

async function handleResponse(response, context) {
    const contentType = response.headers.get("content-type") || "";
    const bodyText = await response.text();

    if (!response.ok) {
        const preview = bodyText ? `: ${bodyText.slice(0, 120)}` : "";
        throw new Error(
            `${context || "Request"} failed (${response.status} ${response.statusText})${preview}`
        );
    }

    if (!contentType.toLowerCase().includes("application/json")) {
        const preview = bodyText ? ` Body starts: ${bodyText.slice(0, 120)}` : "";
        throw new Error(
            `${context || "Request"} returned non-JSON (${contentType || "unknown"}).${preview}`
        );
    }

    try {
        return JSON.parse(bodyText);
    } catch (error) {
        throw new Error(
            `${context || "Request"} returned invalid JSON: ${error?.message || error}`
        );
    }
}

async function handleCrmResponse(response, context) {
    const contentType = response.headers.get("content-type") || "";
    const bodyText = await response.text();
    const isJson = contentType.toLowerCase().includes("application/json");

    if (!response.ok) {
        let parsedError = null;
        if (isJson && bodyText) {
            try {
                const data = JSON.parse(bodyText);
                parsedError =
                    data?.error ||
                    data?.message ||
                    (Array.isArray(data?.errors) && data.errors.length
                        ? data.errors.map((item) => item.message).filter(Boolean).join(" ")
                        : null);
            } catch (error) {
                parsedError = null;
            }
        }

        if (parsedError) {
            throw new Error(parsedError);
        }

        const preview = bodyText ? ` ${bodyText.slice(0, 120)}` : "";
        throw new Error(
            `${context || "Request"} failed (${response.status} ${response.statusText}).${preview}`
        );
    }

    if (!isJson) {
        const preview = bodyText ? ` Body starts: ${bodyText.slice(0, 120)}` : "";
        throw new Error(
            `${context || "Request"} returned non-JSON (${contentType || "unknown"}).${preview}`
        );
    }

    try {
        return JSON.parse(bodyText);
    } catch (error) {
        throw new Error(
            `${context || "Request"} returned invalid JSON: ${error?.message || error}`
        );
    }
}

function normalizeCredential(raw) {
    if (!raw) return null;
    const loginSuccessfulRaw =
        raw["login-successful"] ?? raw.login_successful ?? raw.loginSuccessful ?? false;
    const expiresAtRaw = raw.expires_at ?? raw.expiresAt ?? raw["expires-at"];
    const expiresAtParsed = expiresAtRaw ? new Date(expiresAtRaw) : null;
    const expiresAt =
        expiresAtParsed && !Number.isNaN(expiresAtParsed.getTime())
            ? expiresAtParsed.toISOString()
            : null;
    return {
        id: raw.id,
        username: raw.username,
        company: raw.company,
        provider: raw.provider,
        first_name: raw.first_name ?? raw.firstName ?? raw["first-name"] ?? null,
        last_name: raw.last_name ?? raw.lastName ?? raw["last-name"] ?? null,
        comments: raw.comments ?? raw.comment ?? null,
        duration: raw.duration ?? null,
        expires_at: expiresAt,
        "login-successful":
            typeof loginSuccessfulRaw === "string"
                ? loginSuccessfulRaw.toLowerCase() === "true"
                : Boolean(loginSuccessfulRaw),
        "created-at": raw["created-at"] ?? raw.created_at ?? raw.createdAt ?? null,
    };
}

function toSupabaseCredentialPayload(input, ownerId) {
    const payload = {
        username: input.username ?? "",
        company: input.company ?? "",
        provider: input.provider ?? "",
        login_successful:
            typeof input["login-successful"] === "string"
                ? input["login-successful"].toLowerCase() === "true"
                : typeof input.login_successful === "string"
                  ? input.login_successful.toLowerCase() === "true"
                  : typeof input.loginSuccessful === "string"
                    ? input.loginSuccessful.toLowerCase() === "true"
                    : Boolean(
                          input["login-successful"] ??
                              input.login_successful ??
                              input.loginSuccessful ??
                              false
                      ),
    };

    if (ownerId) {
        payload.owner = ownerId;
    } else if (input.owner) {
        payload.owner = input.owner;
    }

    const expiresAt = input.expires_at ?? input.expiresAt;
    if (expiresAt !== undefined) {
        if (expiresAt === null) {
            payload.expires_at = null;
        } else {
            const parsed = new Date(expiresAt);
            payload.expires_at = Number.isNaN(parsed.getTime())
                ? null
                : parsed.toISOString();
        }
    }

    if (input.duration !== undefined) {
        payload.duration = input.duration;
    }

    if (input.first_name !== undefined || input.firstName !== undefined) {
        payload.first_name = input.first_name ?? input.firstName ?? null;
    }

    if (input.last_name !== undefined || input.lastName !== undefined) {
        payload.last_name = input.last_name ?? input.lastName ?? null;
    }

    if (input.comments !== undefined) {
        payload.comments = input.comments;
    }

    return payload;
}

function supabaseError(action, error) {
    const message = error?.message || error;
    return new Error(`Supabase ${action} failed: ${message}`);
}

async function getCurrentUserId({ requireConfirmed = false } = {}) {
    const supabase = getSupabaseClient();
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
        throw supabaseError("get current session", sessionError);
    }
    const session = sessionData?.session;
    const user = session?.user;
    if (!user?.id) {
        throw new Error("Please sign in to continue.");
    }
    if (requireConfirmed && !user.email_confirmed_at) {
        throw new Error("Please confirm your email before accessing passwords.");
    }
    return user.id;
}

const IPV4_REGEX =
    /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

function isValidIpv6(address) {
    if (!address) return false;
    const lower = address.toLowerCase();
    if (!/^[0-9a-f:]+$/.test(lower)) {
        return false;
    }

    if (lower.includes("::") && lower.indexOf("::") !== lower.lastIndexOf("::")) {
        return false;
    }

    const parts = lower.split(":");
    if (!lower.includes("::") && parts.length !== 8) {
        return false;
    }

    const filtered = parts.filter(Boolean);
    if (lower.includes("::") && filtered.length > 7) {
        return false;
    }

    return parts.every((part) => {
        if (!part) return true;
        return /^[0-9a-f]{1,4}$/.test(part);
    });
}

function normalizeClientIp(value) {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;

    const lower = trimmed.toLowerCase();
    if (lower.startsWith("::ffff:")) {
        const maybeIpv4 = trimmed.slice(7);
        if (IPV4_REGEX.test(maybeIpv4)) {
            return maybeIpv4;
        }
    }

    if (IPV4_REGEX.test(trimmed) || isValidIpv6(trimmed)) {
        return trimmed;
    }

    return null;
}

function sanitizeUserAgentString(value) {
    if (!value) return null;
    const raw = String(value);
    let cleaned = "";
    for (let i = 0; i < raw.length; i += 1) {
        const code = raw.charCodeAt(i);
        if (code >= 32 && code !== 127) {
            cleaned += raw[i];
        }
    }
    cleaned = cleaned.trim();
    if (!cleaned) return null;
    return cleaned.length > MAX_USER_AGENT_LENGTH
        ? cleaned.slice(0, MAX_USER_AGENT_LENGTH)
        : cleaned;
}

let clientMetadataCache = null;
let clientMetadataPromise = null;

async function getClientMetadata() {
    if (clientMetadataCache) {
        return clientMetadataCache;
    }

    if (clientMetadataPromise) {
        return clientMetadataPromise;
    }

    clientMetadataPromise = (async () => {
        const userAgent = sanitizeUserAgentString(
            typeof navigator !== "undefined" ? navigator.userAgent : ""
        );
        let ip = null;

        try {
            const clientInfoUrl = `${API_BASE_URL || ""}/api/client-info`;
            const response = await fetch(clientInfoUrl, { method: "GET" });
            const data = await handleResponse(response, "Client metadata");
            ip = normalizeClientIp(data?.ip);
        } catch (error) {
            console.warn("Unable to fetch client metadata", error);
        }

        const metadata = { ip, userAgent };
        clientMetadataCache = metadata;
        clientMetadataPromise = null;
        return metadata;
    })();

    return clientMetadataPromise;
}

const VALID_INVITATION_STATUSES = new Set([
    "pending",
    "completed",
    "expired",
    "cancelled",
]);

async function requireInternalStaff() {
    const supabase = getSupabaseClient();
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
        .from("profiles")
        .select("id, email, full_name, role")
        .eq("id", userId)
        .maybeSingle();

    if (error) {
        throw supabaseError("fetch profile", error);
    }

    if (!data || data.role !== "internal_staff") {
        throw new Error("Invitations are internal-only.");
    }

    return { userId, profile: data };
}

async function getSupabaseAccessToken() {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.getSession();
    if (error) {
        throw supabaseError("get current session", error);
    }
    return data?.session?.access_token || null;
}

const normalizeInvitation = (invitation) => {
    if (!invitation) return null;
    return {
        id: invitation.id,
        created_by: invitation.created_by,
        client_email: invitation.client_email,
        client_first_name: invitation.client_first_name,
        client_last_name: invitation.client_last_name,
        company: invitation.company,
        provider: invitation.provider,
        status: invitation.status,
        credential_id: invitation.credential_id,
        expires_at: invitation.expires_at,
        completed_at: invitation.completed_at,
        created_at: invitation.created_at,
        updated_at: invitation.updated_at,
        created_by_profile: invitation.created_by_profile || null,
    };
};

function isValidStatusTransition(currentStatus, nextStatus) {
    if (!VALID_INVITATION_STATUSES.has(nextStatus)) {
        return false;
    }

    if (currentStatus === nextStatus) {
        return true;
    }

    if (currentStatus === "cancelled" || currentStatus === "completed") {
        return false;
    }

    if (currentStatus === "expired") {
        return nextStatus === "pending";
    }

    if (currentStatus === "pending") {
        return ["cancelled", "completed"].includes(nextStatus);
    }

    return false;
}

// Supabase implementations
async function supabaseFetchCredentials() {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from(SUPABASE_CREDENTIALS_TABLE)
        .select(
            "id, username, company, provider, login_successful, created_at, expires_at, duration, first_name, last_name, comments"
        );

    if (error) {
        throw supabaseError("fetch credentials", error);
    }
    return (data || []).map(normalizeCredential).filter(Boolean);
}

async function supabaseFetchCredentialById(id) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from(SUPABASE_CREDENTIALS_TABLE)
        .select(
            "id, username, company, provider, login_successful, created_at, expires_at, duration, first_name, last_name, comments"
        )
        .eq("id", id)
        .maybeSingle();

    if (error) {
        throw supabaseError("fetch credential", error);
    }
    return normalizeCredential(data);
}

async function supabaseFetchPasswordByCredentialId(id) {
    await getCurrentUserId({ requireConfirmed: true });
    let metadata = {};
    try {
        metadata = await getClientMetadata();
    } catch (error) {
        console.warn("Unable to resolve client metadata", error);
    }
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc("get_password", {
        p_credential_id: id,
        p_ip: metadata?.ip ?? null,
        p_user_agent: metadata?.userAgent ?? null,
    });

    if (error) {
        throw supabaseError("fetch password", error);
    }
    return data ?? null;
}

async function supabaseUpsertPassword(credentialId, password) {
    if (typeof password !== "string") return null;

    await getCurrentUserId({ requireConfirmed: false });
    let metadata = {};
    try {
        metadata = await getClientMetadata();
    } catch (error) {
        console.warn("Unable to resolve client metadata", error);
    }
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc("set_password", {
        p_credential_id: credentialId,
        p_password: password,
        p_ip: metadata?.ip ?? null,
        p_user_agent: metadata?.userAgent ?? null,
    });

    if (error) {
        throw supabaseError("save password", error);
    }
    return password;
}

async function supabaseCreateCredential(payload) {
    const supabase = getSupabaseClient();
    const ownerId = await getCurrentUserId();
    const credentialPayload = toSupabaseCredentialPayload(payload, ownerId);

    const { data, error } = await supabase
        .from(SUPABASE_CREDENTIALS_TABLE)
        .insert(credentialPayload)
        .select(
            "id, username, company, provider, login_successful, created_at, expires_at, duration, first_name, last_name, comments"
        )
        .single();

    if (error) {
        throw supabaseError("create credential", error);
    }

    if (payload.password) {
        await supabaseUpsertPassword(data.id, payload.password);
    }

    return normalizeCredential(data);
}

async function supabaseUpdateCredential(id, payload) {
    const supabase = getSupabaseClient();
    const { owner, ...credentialPayload } = toSupabaseCredentialPayload(payload);

    const { data, error } = await supabase
        .from(SUPABASE_CREDENTIALS_TABLE)
        .update(credentialPayload)
        .eq("id", id)
        .select(
            "id, username, company, provider, login_successful, created_at, expires_at, duration, first_name, last_name, comments"
        )
        .single();

    if (error) {
        throw supabaseError("update credential", error);
    }

    if (payload.password) {
        await supabaseUpsertPassword(id, payload.password);
    }

    return normalizeCredential(data);
}

async function extendCredentialExpiration(credentialId, newDuration) {
    if (!newDuration) {
        throw new Error("Expiration duration is required.");
    }

    await requireInternalStaff();
    const supabase = getSupabaseClient();
    const { data: current, error: currentError } = await supabase
        .from(SUPABASE_CREDENTIALS_TABLE)
        .select("expires_at")
        .eq("id", credentialId)
        .maybeSingle();

    if (currentError) {
        throw supabaseError("fetch credential expiration", currentError);
    }

    if (!current) {
        throw new Error("Credential not found.");
    }

    const now = new Date();
    let baseDate = now;
    if (current.expires_at) {
        const currentDate = new Date(current.expires_at);
        if (!Number.isNaN(currentDate.getTime()) && currentDate > now) {
            baseDate = currentDate;
        }
    }

    const expiresAt = calculateExpirationDate(newDuration, baseDate);
    const updatePayload = {
        duration: newDuration,
        expires_at: expiresAt ? expiresAt.toISOString() : null,
    };

    const { data, error } = await supabase
        .from(SUPABASE_CREDENTIALS_TABLE)
        .update(updatePayload)
        .eq("id", credentialId)
        .select(
            "id, username, company, provider, login_successful, created_at, expires_at, duration, first_name, last_name, comments"
        )
        .single();

    if (error) {
        throw supabaseError("extend expiration", error);
    }

    return normalizeCredential(data);
}

async function supabaseDeleteCredential(id) {
    const supabase = getSupabaseClient();

    await getCurrentUserId({ requireConfirmed: true });
    let metadata = {};
    try {
        metadata = await getClientMetadata();
    } catch (error) {
        console.warn("Unable to resolve client metadata", error);
    }
    const { error: passwordError } = await supabase.rpc("delete_password", {
        p_credential_id: id,
        p_ip: metadata?.ip ?? null,
        p_user_agent: metadata?.userAgent ?? null,
    });

    if (passwordError) {
        throw supabaseError("delete password", passwordError);
    }

    const { error } = await supabase
        .from(SUPABASE_CREDENTIALS_TABLE)
        .delete()
        .eq("id", id);

    if (error) {
        throw supabaseError("delete credential", error);
    }

    return true;
}

async function createInvitation(payload) {
    if (!USE_SUPABASE) {
        throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to create invitations.");
    }

    const safePayload = payload || {};
    const token = await getSupabaseAccessToken();
    if (!token) {
        throw new Error("Please sign in to continue.");
    }

    const clientEmail = String(safePayload.clientEmail || "").trim().toLowerCase();
    const clientFirstName = String(safePayload.clientFirstName || "").trim();
    const clientLastName = String(safePayload.clientLastName || "").trim();
    const company = String(safePayload.company || "").trim();
    const provider = String(safePayload.provider || "").trim();

    const body = {
        clientEmail,
        clientFirstName,
        clientLastName,
        company,
        provider,
        expiresInDays: safePayload.expiresInDays,
        crmRecordId: safePayload.crmRecordId,
        crmModule: safePayload.crmModule,
    };

    const response = await fetch(`${API_BASE_URL || ""}/invitations`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    });

    const payloadData = await handleResponse(response, "Create invitation");
    if (!payloadData?.invitationId) {
        throw new Error("Invitation response did not include an ID.");
    }

    return {
        id: payloadData.invitationId,
        deliveryStatus: payloadData.deliveryStatus || "unknown",
    };
}

async function searchCrmContactsByEmail(email) {
    const query = String(email || "").trim();
    if (!query) {
        return [];
    }

    const token = await getSupabaseAccessToken();
    if (!token) {
        throw new Error("Please sign in to continue.");
    }

    const response = await fetch(
        `${API_BASE_URL || ""}/api/crm/search/contacts?email=${encodeURIComponent(
            query
        )}&_=${Date.now()}`,
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Cache-Control": "no-store",
            },
            cache: "no-store",
        }
    );

    const payload = await handleCrmResponse(response, "CRM contact search");
    if (payload?.success === false) {
        throw new Error(payload?.error || "CRM contact search failed.");
    }
    return Array.isArray(payload?.data) ? payload.data : [];
}

async function searchCrmAccountsByCompany(company) {
    const query = String(company || "").trim();
    if (!query) {
        return [];
    }

    const token = await getSupabaseAccessToken();
    if (!token) {
        throw new Error("Please sign in to continue.");
    }

    const response = await fetch(
        `${API_BASE_URL || ""}/api/crm/search/accounts?company=${encodeURIComponent(
            query
        )}&_=${Date.now()}`,
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Cache-Control": "no-store",
            },
            cache: "no-store",
        }
    );

    const payload = await handleCrmResponse(response, "CRM account search");
    if (payload?.success === false) {
        throw new Error(payload?.error || "CRM account search failed.");
    }
    return Array.isArray(payload?.data) ? payload.data : [];
}

async function fetchCrmAccountPrimaryContact(accountId) {
    const resolvedId = String(accountId || "").trim();
    if (!resolvedId) {
        return null;
    }

    const token = await getSupabaseAccessToken();
    if (!token) {
        throw new Error("Please sign in to continue.");
    }

    const response = await fetch(
        `${API_BASE_URL || ""}/api/crm/accounts/${encodeURIComponent(
            resolvedId
        )}/primary-contact?_=${Date.now()}`,
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Cache-Control": "no-store",
            },
            cache: "no-store",
        }
    );

    const payload = await handleCrmResponse(
        response,
        "CRM account contact lookup"
    );
    if (payload?.success === false) {
        throw new Error(payload?.error || "CRM account contact lookup failed.");
    }
    return payload?.data || null;
}

async function fetchInvitations({ status, limit = 50, offset = 0 } = {}) {
    if (!USE_SUPABASE) {
        throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to fetch invitations.");
    }

    await requireInternalStaff();
    const supabase = getSupabaseClient();

    let query = supabase
        .from(SUPABASE_INVITATIONS_TABLE)
        .select(
            "id, created_by, client_email, client_first_name, client_last_name, company, provider, status, credential_id, expires_at, completed_at, created_at, updated_at",
            { count: "exact" }
        )
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

    if (status && status !== "all") {
        query = query.eq("status", status);
    }

    const { data, error, count } = await query;

    if (error) {
        throw supabaseError("fetch invitations", error);
    }

    const invitations = (data || []).map(normalizeInvitation).filter(Boolean);
    const creatorIds = [
        ...new Set(invitations.map((invitation) => invitation.created_by)),
    ].filter(Boolean);

    let profileMap = new Map();
    if (creatorIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
            .from("profiles")
            .select("id, email, full_name, role")
            .in("id", creatorIds);

        if (profileError) {
            throw supabaseError("fetch invitation creators", profileError);
        }

        profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]));
    }

    const enriched = invitations.map((invitation) => ({
        ...invitation,
        created_by_profile: profileMap.get(invitation.created_by) || null,
    }));

    return {
        data: enriched,
        count: count ?? enriched.length,
    };
}

async function fetchInvitationById(id) {
    if (!USE_SUPABASE) {
        throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to fetch invitations.");
    }

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from(SUPABASE_INVITATIONS_TABLE)
        .select(
            "id, created_by, client_email, client_first_name, client_last_name, company, provider, status, credential_id, expires_at, completed_at, created_at, updated_at"
        )
        .eq("id", id)
        .maybeSingle();

    if (error) {
        throw supabaseError("fetch invitation", error);
    }

    return normalizeInvitation(data);
}

async function updateInvitationStatus(id, status) {
    if (!USE_SUPABASE) {
        throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to update invitations.");
    }

    await requireInternalStaff();
    const supabase = getSupabaseClient();

    const invitation = await fetchInvitationById(id);
    if (!invitation) {
        throw new Error("Invitation not found.");
    }

    if (!isValidStatusTransition(invitation.status, status)) {
        throw new Error(
            `Cannot change invitation from ${invitation.status} to ${status}.`
        );
    }

    const { data, error } = await supabase
        .from(SUPABASE_INVITATIONS_TABLE)
        .update({ status })
        .eq("id", id)
        .select(
            "id, created_by, client_email, client_first_name, client_last_name, company, provider, status, credential_id, expires_at, completed_at, created_at, updated_at"
        )
        .single();

    if (error) {
        throw supabaseError("update invitation", error);
    }

    return normalizeInvitation(data);
}

async function resendInvitation(id) {
    if (!USE_SUPABASE) {
        throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to resend invitations.");
    }

    const token = await getSupabaseAccessToken();
    if (!token) {
        throw new Error("Please sign in to continue.");
    }

    const response = await fetch(`${API_BASE_URL || ""}/invitations/${id}/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
    });
    return handleResponse(response, "Resend invitation");
}

async function supabaseFetchAccessLogs({ limit = 50, offset = 0 } = {}) {
    await getCurrentUserId({ requireConfirmed: true });
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc("fetch_access_logs", {
        p_limit: limit,
        p_offset: offset,
    });

    if (error) {
        throw supabaseError("fetch access logs", error);
    }
    return data || [];
}

// Mock implementations using local fixtures in /public/DEMO_DATA
async function mockFetchCredentials() {
    await delay();
    const response = await fetch(joinUrl(DEMO_DATA_BASE_URL, "CREDENTIALS.json"));
    const data = await handleResponse(response, "Mock credentials");
    const credentials = Array.isArray(data) ? data : [];
    return credentials.map(normalizeCredential).filter(Boolean);
}

async function mockFetchCredentialById(id) {
    const all = await mockFetchCredentials();
    return (
        all.find((item) => String(item.id) === String(id)) ??
        null
    );
}

async function mockFetchPasswordByCredentialId(id) {
    await delay();
    const response = await fetch(joinUrl(DEMO_DATA_BASE_URL, "PASSWORDS.json"));
    const data = await handleResponse(response, "Mock password");
    const entry = Array.isArray(data)
        ? data.find((item) => String(item.credential_id) === String(id))
        : null;
    return entry?.password ?? null;
}

// Public API surface
export async function fetchCredentials() {
    if (USE_SUPABASE) {
        return supabaseFetchCredentials();
    }
    if (!USE_MOCK_API && API_BASE_URL) {
        const response = await fetch(`${API_BASE_URL}/credentials`);
        return handleResponse(response, "Credentials");
    }
    return mockFetchCredentials();
}

export async function fetchCredentialById(id) {
    if (USE_SUPABASE) {
        return supabaseFetchCredentialById(id);
    }
    if (!USE_MOCK_API && API_BASE_URL) {
        const response = await fetch(`${API_BASE_URL}/credentials/${id}`);
        return handleResponse(response, "Credential");
    }
    return mockFetchCredentialById(id);
}

export async function fetchPasswordByCredentialId(id) {
    if (USE_SUPABASE) {
        return supabaseFetchPasswordByCredentialId(id);
    }
    if (!USE_MOCK_API && API_BASE_URL) {
        const response = await fetch(`${API_BASE_URL}/credentials/${id}/password`);
        const data = await handleResponse(response, "Password");
        return data?.password ?? null;
    }
    return mockFetchPasswordByCredentialId(id);
}

export async function createCredential(payload) {
    if (USE_SUPABASE) {
        return supabaseCreateCredential(payload);
    }
    throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to create credentials.");
}

export async function updateCredential(id, payload) {
    if (USE_SUPABASE) {
        return supabaseUpdateCredential(id, payload);
    }
    throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to edit credentials.");
}

export async function deleteCredential(id) {
    if (USE_SUPABASE) {
        return supabaseDeleteCredential(id);
    }
    throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to delete credentials.");
}

export async function fetchAccessLogs(options) {
    if (USE_SUPABASE) {
        return supabaseFetchAccessLogs(options);
    }
    throw new Error("Supabase is not enabled. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to view audit logs.");
}

export async function requestDataDeletion({
    credentialId,
    requesterEmail,
    confirmDeletion,
}) {
    const response = await fetch(
        `${API_BASE_URL}/api/data-privacy/deletion-request`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                credentialId,
                requesterEmail,
                confirmDeletion,
            }),
        }
    );

    return handleResponse(response, "Data deletion request");
}

export async function listDataDeletionRequests({
    status,
    page,
    limit,
    accessToken,
}) {
    if (!accessToken) {
        throw new Error("Access token is required to list deletion requests.");
    }

    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));

    const url = `${API_BASE_URL}/api/data-privacy/deletion-requests${
        params.toString() ? `?${params.toString()}` : ""
    }`;
    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });

    return handleResponse(response, "Deletion requests");
}

export async function processDataDeletionRequest({
    requestId,
    approved,
    reason,
    accessToken,
}) {
    if (!accessToken) {
        throw new Error("Access token is required to process deletion requests.");
    }

    const response = await fetch(
        `${API_BASE_URL}/api/data-privacy/deletion-requests/${requestId}/process`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ approved, reason }),
        }
    );

    return handleResponse(response, "Process deletion request");
}

export {
    USE_MOCK_API,
    USE_SUPABASE,
    API_BASE_URL,
    SUPABASE_CREDENTIALS_TABLE,
    SUPABASE_PASSWORDS_TABLE,
    SUPABASE_INVITATIONS_TABLE,
    searchCrmContactsByEmail,
    searchCrmAccountsByCompany,
    fetchCrmAccountPrimaryContact,
    createInvitation,
    fetchInvitations,
    fetchInvitationById,
    updateInvitationStatus,
    resendInvitation,
    extendCredentialExpiration,
};
